import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import PricingSection from './components/PricingSection';
import RaiseTicket from './pages/RaiseTicket';
import Shop from './pages/Shop';
import CategoryPage from './pages/CategoryPage';
import WhatsAppButton from './components/WhatsAppButton';
import chesiLogo from './chesi-logo.png';
import chesiwmsHome from './chesiwms home.png';
import isuzuLogo from './isuzu.png';
import aristocLogo from './aristoc.PNG';
import martNetworksLogo from './mart-networks.png';
import manixClothingLogo from './manix-clothing.png';
import niaMotorsLogo from './nia-motors.png';
import spiroEvLogo from './spiro-logo.jpg';
import gs1Logo from './gs1-logo.png';
import honeywellLogo from './honeywell-logo.png';
import quickbooksLogo from './quickbooks-logo.png';
import sageLogo from './sage-logo.png';
import sapLogo from './sap-logo.jpg';

function AppContent() {
  return (
    <Router>
      <Routes>
        <Route path="/raise-ticket" element={<RaiseTicket />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/category/:categoryName" element={<CategoryPage />} />
        <Route path="/" element={<HomePage />} />
      </Routes>
      <WhatsAppButton />
    </Router>
  );
}

function App() {
  return <AppContent />;
}

function HomePage() {
  const [activeTab, setActiveTab] = useState('warehousing');
  const [terminalText, setTerminalText] = useState('');
  const [solutionsDropdown, setSolutionsDropdown] = useState(false);
  const fadeElementsRef = useRef([]);
  const bestSellingHardware = [
    {
      name: 'Honeywell EDA52',
      category: 'Mobile Computer',
      description: 'Rugged Android handheld for fast picking, receiving, and stock checks.',
      highlight: 'Most requested',
      image: '/assets/images/EDA52.png',
      imageFallback: '/assets/images/EDA52.jpg'
    },
    {
      name: 'Honeywell CK65',
      category: 'Warehouse Scanner',
      description: 'Ultra-durable scanner computer built for long shifts and harsh environments.',
      highlight: 'High durability',
      image: '/assets/images/CK65.png',
      imageFallback: '/assets/images/Ck65.jpg'
    },
    {
      name: 'Honeywell PD45',
      category: 'Industrial Printer',
      description: 'Reliable label printing for barcodes, bins, pallets, and dispatch workflows.',
      highlight: 'Fast print speed',
      image: 'https://honeywell.scene7.com/is/image/Honeywell65/sps-ppr-pd45s-printer-main-lowres?fmt=png-alpha',
      imageFallback: '/assets/images/PD45.jpg'
    },
    {
      name: 'Honeywell RT10A',
      category: 'Rugged Tablet',
      description: 'Large-screen tablet for forklift ops, dispatch management, and floor visibility.',
      highlight: 'Field ready',
      image: '/assets/images/RT10A.png',
      imageFallback: '/assets/images/RT10A.jpg'
    }
  ];

  const terminalLines = [
    "> Loading inventory data...",
    "> Syncing with ERP...",
    "> Processing 42 pending orders...",
    "> Optimizing picking routes...",
    "> Ready for operations"
  ];

  useEffect(() => {
    // Fade-in animation on scroll
    const fadeInOnScroll = () => {
      fadeElementsRef.current.forEach((element) => {
        if (element) {
          const elementTop = element.getBoundingClientRect().top;
          const elementVisible = 150;
          
          if (elementTop < window.innerHeight - elementVisible) {
            element.classList.add('visible');
          }
        }
      });
    };

    window.addEventListener('scroll', fadeInOnScroll);
    fadeInOnScroll(); // Trigger on load

    return () => window.removeEventListener('scroll', fadeInOnScroll);
  }, []);

  useEffect(() => {
    // Terminal typing effect
    let currentLine = 0;
    let currentChar = 0;
    let isDeleting = false;
    let terminalText = "";
    let timeoutId;

    const typeTerminal = () => {
      const currentText = terminalLines[currentLine];

      if (isDeleting) {
        terminalText = currentText.substring(0, currentChar - 1);
        currentChar--;
      } else {
        terminalText = currentText.substring(0, currentChar + 1);
        currentChar++;
      }

      setTerminalText(terminalText);

      if (!isDeleting && currentChar === currentText.length) {
        isDeleting = true;
        timeoutId = setTimeout(typeTerminal, 2000);
      } else if (isDeleting && currentChar === 0) {
        isDeleting = false;
        currentLine = (currentLine + 1) % terminalLines.length;
        timeoutId = setTimeout(typeTerminal, 500);
      } else {
        const speed = isDeleting ? 50 : 100;
        timeoutId = setTimeout(typeTerminal, speed);
      }
    };

    timeoutId = setTimeout(typeTerminal, 1000);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="App grid-pattern overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed w-full z-50 bg-black bg-opacity-90 backdrop-filter backdrop-blur-lg border-b border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <img src={chesiLogo} alt="ChesiWMS Logo" className="h-10 neon-hover" />
              </div>
              <div className="hidden md:block ml-10">
                <div className="flex space-x-8 items-center">
                  <a href="#features" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 glow-on-hover whitespace-nowrap">Features</a>
                  <div 
                    className="relative"
                    onMouseEnter={() => setSolutionsDropdown(true)}
                    onMouseLeave={() => setSolutionsDropdown(false)}
                  >
                    <a href="#solutions" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 glow-on-hover whitespace-nowrap">Solutions</a>
                    {solutionsDropdown && (
                      <div 
                        className="absolute top-full left-1/2 transform -translate-x-1/2 w-[800px] bg-gray-900 border border-gray-800 rounded-lg shadow-2xl p-6 z-50" 
                        style={{marginTop: '8px'}} 
                        onMouseEnter={() => setSolutionsDropdown(true)}
                        onMouseLeave={() => setSolutionsDropdown(false)}
                      >
                        <div className="grid grid-cols-3 gap-4">
                          <a href="#chesiwms" className="mega-menu-item p-4 border border-gray-800 rounded-lg hover:border-gold hover:bg-gray-900 transition-all duration-300">
                            <h4 className="text-white font-semibold text-lg mb-2">ChesiWMS</h4>
                            <p className="text-gray-400 text-sm">Warehouse Management System</p>
                          </a>
                          <a href="#chesiai" className="mega-menu-item p-4 border border-gray-800 rounded-lg hover:border-gold hover:bg-gray-900 transition-all duration-300">
                            <h4 className="text-white font-semibold text-lg mb-2">ChesiAI</h4>
                            <p className="text-gray-400 text-sm">AI Powered Financial Planner</p>
                          </a>
                          <a href="#chesiwaiver" className="mega-menu-item p-4 border border-gray-800 rounded-lg hover:border-gold hover:bg-gray-900 transition-all duration-300">
                            <h4 className="text-white font-semibold text-lg mb-2">ChesiWaiver</h4>
                            <p className="text-gray-400 text-sm">E-Waiver Solution</p>
                          </a>
                          <a href="#chesipos" className="mega-menu-item p-4 border border-gray-800 rounded-lg hover:border-gold hover:bg-gray-900 transition-all duration-300">
                            <h4 className="text-white font-semibold text-lg mb-2">ChesiPOS</h4>
                            <p className="text-gray-400 text-sm">Point of Sale System</p>
                          </a>
                          <a href="#chesivms" className="mega-menu-item p-4 border border-gray-800 rounded-lg hover:border-gold hover:bg-gray-900 transition-all duration-300">
                            <h4 className="text-white font-semibold text-lg mb-2">ChesiVMS</h4>
                            <p className="text-gray-400 text-sm">Venue Management System</p>
                          </a>
                          <a href="#chesitos" className="mega-menu-item p-4 border border-gray-800 rounded-lg hover:border-gold hover:bg-gray-900 transition-all duration-300">
                            <h4 className="text-white font-semibold text-lg mb-2">ChesiTOS</h4>
                            <p className="text-gray-400 text-sm">For ICDs</p>
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                  <Link to="/shop" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 glow-on-hover whitespace-nowrap">Shop</Link>
                  <a href="#pricing" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 glow-on-hover whitespace-nowrap">Pricing</a>
                  <a href="#testimonials" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 glow-on-hover whitespace-nowrap">Testimonials</a>
                  <a href="#integrations" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 glow-on-hover whitespace-nowrap">Integrations</a>
                  <Link to="/raise-ticket" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 glow-on-hover whitespace-nowrap">Support</Link>
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="ml-4 flex items-center space-x-4">
                <a href="#" className="text-gray-300 hover:text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 glow-on-hover whitespace-nowrap">Login</a>
                <a href="#pricing" className="btn-gold px-6 py-2 rounded-md text-sm font-medium whitespace-nowrap">Start Free Trial</a>
              </div>
            </div>
            <div className="md:hidden">
              <button type="button" className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white focus:outline-none">
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden min-h-screen flex items-center">
        <div className="absolute inset-0 circuit-overlay"></div>
        <div className="absolute inset-0 overflow-hidden">
          {/* Matrix lines animation */}
          <div className="matrix-line" style={{left: '10%', animationDuration: '10s', animationDelay: '0s'}}></div>
          <div className="matrix-line" style={{left: '30%', animationDuration: '15s', animationDelay: '2s'}}></div>
          <div className="matrix-line" style={{left: '50%', animationDuration: '12s', animationDelay: '1s'}}></div>
          <div className="matrix-line" style={{left: '70%', animationDuration: '14s', animationDelay: '3s'}}></div>
          <div className="matrix-line" style={{left: '90%', animationDuration: '11s', animationDelay: '4s'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            <div className="sm:text-center md:max-w-2xl md:mx-auto lg:col-span-7 lg:text-left">
              <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl fade-in" ref={el => fadeElementsRef.current.push(el)}>
                <span className="block">Welcome to the Future</span>
                <span className="block text-gold">of Warehousing.</span>
              </h1>
              <p className="mt-6 text-xl text-gray-300 max-w-2xl fade-in" style={{transitionDelay: '0.1s'}} ref={el => fadeElementsRef.current.push(el)}>
                ChesiWMS digitizes your warehouse with real-time tracking, barcode automation, and AI-driven picking strategies - designed for tomorrow's Africa.
              </p>
              <div className="mt-10 sm:max-w-lg sm:mx-auto sm:text-center lg:text-left lg:mx-0">
                <div className="flex flex-col sm:flex-row gap-4 fade-in" style={{transitionDelay: '0.2s'}} ref={el => fadeElementsRef.current.push(el)}>
                  <a href="#pricing" className="btn-gold px-8 py-4 rounded-md text-lg font-medium pulse">
                    Start Free Trial
                  </a>
                  <a href="#" className="btn-ghost px-8 py-4 rounded-md text-lg font-medium">
                    See It In Action
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-16 relative sm:max-w-lg sm:mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:col-span-5 lg:flex lg:items-center fade-in" style={{transitionDelay: '0.3s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="floating neon-border rounded-xl overflow-hidden">
                <img className="w-full" src={chesiwmsHome} alt="ChesiWMS Home Interface" />
              </div>
            </div>
          </div>
        </div>
        <div className="scroll-indicator">
          <svg className="w-8 h-8 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
          </svg>
        </div>
      </section>

      {/* As Seen On */}
      <section className="py-12 bg-black bg-opacity-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-semibold uppercase text-gray-400 tracking-wider mb-8 fade-in" ref={el => fadeElementsRef.current.push(el)}>
            Trusted by industry leaders across Africa
          </p>
          <div className="grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-6">
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.1s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={isuzuLogo} alt="Isuzu East Africa" />
              </div>
            </div>
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.2s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={aristocLogo} alt="Aristoc Booklex Uganda" />
              </div>
            </div>
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.3s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={martNetworksLogo} alt="Mart Networks Distributors" />
              </div>
            </div>
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.4s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={manixClothingLogo} alt="Manix Clothing" />
              </div>
            </div>
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.5s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={niaMotorsLogo} alt="Nia Motors Kenya" />
              </div>
            </div>
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.6s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={spiroEvLogo} alt="Spiro EV" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shop Best Sellers */}
      <section className="py-16 bg-black shop-showcase">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="shop-showcase-panel rounded-2xl p-8 md:p-10">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8 fade-in" ref={el => fadeElementsRef.current.push(el)}>
              <div>
                <h2 className="text-base font-semibold tracking-wider text-gold uppercase">Beyond Software</h2>
                <p className="mt-2 text-3xl font-extrabold text-white sm:text-4xl">
                  Best Selling Hardware in Our Shop
                </p>
                <p className="mt-4 max-w-2xl text-lg text-gray-300">
                  Pair ChesiWMS with proven devices used by top warehouse teams across Africa.
                </p>
              </div>
              <Link to="/shop" className="btn-gold px-6 py-3 rounded-md text-base font-semibold whitespace-nowrap self-start lg:self-auto">
                Explore Full Hardware Shop
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {bestSellingHardware.map((item, index) => (
                <div
                  key={item.name}
                  className="hardware-card rounded-xl p-6 fade-in"
                  style={{ transitionDelay: `${0.1 + index * 0.1}s` }}
                  ref={el => fadeElementsRef.current.push(el)}
                >
                  <div className="hardware-image-wrap mb-4">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="hardware-image"
                      onError={(e) => {
                        if (e.currentTarget.dataset.fallbackTried === 'true') {
                          e.currentTarget.src = `https://via.placeholder.com/320x180?text=${encodeURIComponent(item.name)}`;
                        } else {
                          e.currentTarget.dataset.fallbackTried = 'true';
                          e.currentTarget.src = item.imageFallback;
                        }
                      }}
                    />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gold mb-3">{item.category}</p>
                  <h3 className="text-xl font-bold text-white mb-3">{item.name}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed mb-5">{item.description}</p>
                  <div className="hardware-highlight">{item.highlight}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-in" ref={el => fadeElementsRef.current.push(el)}>
            <h2 className="text-base font-semibold tracking-wider text-gold uppercase">Explore the</h2>
            <p className="mt-2 text-3xl font-extrabold text-white sm:text-4xl">
              ChesiWMS Universe
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-400 mx-auto">
              Six powerful modules designed to transform your warehouse operations
            </p>
          </div>

          <div className="mt-16">
            {/* Feature Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <div 
                className={`feature-tab glass-card rounded-lg px-6 py-3 border border-gray-800 hover:border-gold transition-all duration-300 ${activeTab === 'warehousing' ? 'active' : ''}`}
                onClick={() => setActiveTab('warehousing')}
              >
                <div className="flex items-center">
                  <i className="fas fa-warehouse text-gold mr-2"></i>
                  <span className="text-white font-medium">Warehousing</span>
                </div>
              </div>
              <div 
                className={`feature-tab glass-card rounded-lg px-6 py-3 border border-gray-800 hover:border-gold transition-all duration-300 ${activeTab === 'manufacturing' ? 'active' : ''}`}
                onClick={() => setActiveTab('manufacturing')}
              >
                <div className="flex items-center">
                  <i className="fas fa-industry text-gold mr-2"></i>
                  <span className="text-white font-medium">Manufacturing</span>
                </div>
              </div>
              <div 
                className={`feature-tab glass-card rounded-lg px-6 py-3 border border-gray-800 hover:border-gold transition-all duration-300 ${activeTab === 'retail' ? 'active' : ''}`}
                onClick={() => setActiveTab('retail')}
              >
                <div className="flex items-center">
                  <i className="fas fa-store text-gold mr-2"></i>
                  <span className="text-white font-medium">Retail</span>
                </div>
              </div>
              <div 
                className={`feature-tab glass-card rounded-lg px-6 py-3 border border-gray-800 hover:border-gold transition-all duration-300 ${activeTab === 'logistics' ? 'active' : ''}`}
                onClick={() => setActiveTab('logistics')}
              >
                <div className="flex items-center">
                  <i className="fas fa-qrcode text-gold mr-2"></i>
                  <span className="text-white font-medium">Logistics</span>
                </div>
              </div>
              <div 
                className={`feature-tab glass-card rounded-lg px-6 py-3 border border-gray-800 hover:border-gold transition-all duration-300 ${activeTab === 'picking' ? 'active' : ''}`}
                onClick={() => setActiveTab('picking')}
              >
                <div className="flex items-center">
                  <i className="fas fa-route text-gold mr-2"></i>
                  <span className="text-white font-medium">Picking</span>
                </div>
              </div>
              <div 
                className={`feature-tab glass-card rounded-lg px-6 py-3 border border-gray-800 hover:border-gold transition-all duration-300 ${activeTab === 'delivery' ? 'active' : ''}`}
                onClick={() => setActiveTab('delivery')}
              >
                <div className="flex items-center">
                  <i className="fas fa-truck text-gold mr-2"></i>
                  <span className="text-white font-medium">Delivery</span>
                </div>
              </div>
            </div>

            {/* Feature Content */}
            <div className="glass-card rounded-xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">
                    <i className="fas fa-warehouse text-gold mr-3"></i>
                    Warehouse Management
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="hexagon bg-gray-900 w-8 h-8 flex items-center justify-center">
                          <i className="fas fa-boxes text-gold"></i>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium text-white">Bin Replenishment</h4>
                        <p className="mt-1 text-gray-400">Automated restocking of picking locations based on predefined rules</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="hexagon bg-gray-900 w-8 h-8 flex items-center justify-center">
                          <i className="fas fa-clipboard-check text-gold"></i>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium text-white">Cycle Counting</h4>
                        <p className="mt-1 text-gray-400">Continuous inventory verification without disrupting operations</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <div className="hexagon bg-gray-900 w-8 h-8 flex items-center justify-center">
                          <i className="fas fa-barcode text-gold"></i>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium text-white">Barcode Parsing</h4>
                        <p className="mt-1 text-gray-400">Advanced scanning technology for GS1, QR, and custom barcodes</p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="flex items-center justify-center">
                  <div className="terminal rounded-lg p-4 w-full h-64 overflow-hidden">
                    <div className="text-green-400 mb-4">{'>'} Initializing ChesiWMS v3.2...</div>
                    <div className="text-green-400 mb-4">{'>'} Scanning warehouse layout...</div>
                    <div className="text-green-400 mb-4">{'>'} Connected to 14 IoT devices</div>
                    <div className="text-green-400 mb-4">{'>'} Syncing with ERP systems...</div>
                    <div className="text-green-400 mb-4">{'>'} Ready for operations <span className="terminal-cursor">|</span></div>
                    {terminalText && (
                      <div className="text-green-400">{terminalText}<span className="terminal-cursor">|</span></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-in" ref={el => fadeElementsRef.current.push(el)}>
            <h2 className="text-base font-semibold tracking-wider text-gold uppercase">Industry Solutions</h2>
            <p className="mt-2 text-3xl font-extrabold text-white sm:text-4xl">
              Built for Your Business
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-400 mx-auto">
              Tailored solutions for retail, manufacturing, and logistics
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* Retail */}
            <div className="glass-card rounded-xl p-8 fade-in" style={{transitionDelay: '0.1s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="flex items-center mb-6">
                <div className="hexagon bg-gray-800 w-12 h-12 flex items-center justify-center">
                  <i className="fas fa-store text-2xl text-gold"></i>
                </div>
                <h3 className="text-2xl font-bold text-white ml-4">Retail</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Multi-location inventory tracking</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Automated label printing</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Real-time stock lookup</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Seasonal demand forecasting</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Manufacturing */}
            <div className="glass-card rounded-xl p-8 fade-in" style={{transitionDelay: '0.2s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="flex items-center mb-6">
                <div className="hexagon bg-gray-800 w-12 h-12 flex items-center justify-center">
                  <i className="fas fa-industry text-2xl text-gold"></i>
                </div>
                <h3 className="text-2xl font-bold text-white ml-4">Manufacturing</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Work order flows</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Real-time mobile validation</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Raw material tracking</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Quality control integration</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Logistics */}
            <div className="glass-card rounded-xl p-8 fade-in" style={{transitionDelay: '0.3s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="flex items-center mb-6">
                <div className="hexagon bg-gray-800 w-12 h-12 flex items-center justify-center">
                  <i className="fas fa-truck-fast text-2xl text-gold"></i>
                </div>
                <h3 className="text-2xl font-bold text-white ml-4">Logistics</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Route optimization</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Carrier management</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Proof of delivery</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Fleet integration</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection fadeElementsRef={fadeElementsRef} />

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-in" ref={el => fadeElementsRef.current.push(el)}>
            <h2 className="text-base font-semibold tracking-wider text-gold uppercase">Success Stories</h2>
            <p className="mt-2 text-3xl font-extrabold text-white sm:text-4xl neon-text">
              Warehouse Commanders Speak
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-400 mx-auto">
              Hear from businesses that transformed their operations
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* Testimonial 1 */}
            <div className="holographic rounded-xl p-8 fade-in" style={{transitionDelay: '0.1s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="flex items-center mb-6">
                <div className="hexagon bg-gray-800 w-12 h-12 flex items-center justify-center">
                  <i className="fas fa-user text-xl text-gold"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-white">James Mwangi</h3>
                  <p className="text-gray-400">Logistics Manager</p>
                </div>
              </div>
              <div className="flex mb-4">
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
              </div>
              <p className="text-gray-300 italic">
                "Cycle counting used to take 2 days. Now it's 2 hours — thanks to ChesiWMS. The mobile app has transformed how our team works."
              </p>
              <div className="mt-6 pt-6 border-t border-gray-800">
                <img className="h-8 opacity-70" src="https://via.placeholder.com/150x60?text=Company+Logo" alt="Company Logo" />
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="holographic rounded-xl p-8 fade-in" style={{transitionDelay: '0.2s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="flex items-center mb-6">
                <div className="hexagon bg-gray-800 w-12 h-12 flex items-center justify-center">
                  <i className="fas fa-user text-xl text-gold"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-white">Sarah Kamau</h3>
                  <p className="text-gray-400">Operations Director</p>
                </div>
              </div>
              <div className="flex mb-4">
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
              </div>
              <p className="text-gray-300 italic">
                "We reduced picking errors by 87% in the first month. The AI-driven picking strategies are game-changing for our retail business."
              </p>
              <div className="mt-6 pt-6 border-t border-gray-800">
                <img className="h-8 opacity-70" src="https://via.placeholder.com/150x60?text=Company+Logo" alt="Company Logo" />
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="holographic rounded-xl p-8 fade-in" style={{transitionDelay: '0.3s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="flex items-center mb-6">
                <div className="hexagon bg-gray-800 w-12 h-12 flex items-center justify-center">
                  <i className="fas fa-user text-xl text-gold"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-white">David Ochieng</h3>
                  <p className="text-gray-400">CEO</p>
                </div>
              </div>
              <div className="flex mb-4">
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star text-gold"></i>
                <i className="fas fa-star-half-alt text-gold"></i>
              </div>
              <p className="text-gray-300 italic">
                "The real-time tracking gave us visibility we never had before. Our warehouse efficiency improved by 65% after implementing ChesiWMS."
              </p>
              <div className="mt-6 pt-6 border-t border-gray-800">
                <img className="h-8 opacity-70" src="https://via.placeholder.com/150x60?text=Company+Logo" alt="Company Logo" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section id="integrations" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-in" ref={el => fadeElementsRef.current.push(el)}>
            <h2 className="text-base font-semibold tracking-wider text-gold uppercase">Seamless Connectivity</h2>
            <p className="mt-2 text-3xl font-extrabold text-white sm:text-4xl">
              Works With Your Stack
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-400 mx-auto">
              Integrate with leading ERP, barcode readers, and business systems
            </p>
          </div>

          <div className="mt-16 grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-5">
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.1s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={sapLogo} alt="SAP" />
              </div>
            </div>
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.2s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={quickbooksLogo} alt="QuickBooks" />
              </div>
            </div>
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.3s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={gs1Logo} alt="GS1" />
              </div>
            </div>
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.4s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={honeywellLogo} alt="Honeywell" />
              </div>
            </div>
            <div className="col-span-1 flex justify-center items-center fade-in" style={{transitionDelay: '0.5s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="w-32 h-20 bg-white rounded-lg flex justify-center items-center p-4 shadow-md hover:shadow-lg transition-all duration-300">
                <img className="max-w-full max-h-full object-contain" src={sageLogo} alt="Sage" />
              </div>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="fade-in" style={{transitionDelay: '0.1s'}} ref={el => fadeElementsRef.current.push(el)}>
              <h3 className="text-2xl font-bold text-white mb-6">Custom API Integration</h3>
              <p className="text-gray-400 mb-6">
                ChesiWMS offers robust API endpoints to connect with your existing systems. Whether you need real-time inventory updates, order synchronization, or custom reporting, our API makes it possible.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">RESTful JSON API</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Webhook Support</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">OAuth 2.0 Authentication</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <i className="fas fa-check-circle text-gold"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-gray-300">Developer Documentation</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="fade-in" style={{transitionDelay: '0.2s'}} ref={el => fadeElementsRef.current.push(el)}>
              <div className="terminal rounded-lg p-6 w-full h-64 overflow-hidden">
                <div className="text-green-400 mb-2">{'> curl -X POST \\'}</div>
                <div className="text-green-400 mb-2 pl-8">{'-H "Authorization: Bearer $TOKEN" \\'}</div>
                <div className="text-green-400 mb-2 pl-8">{'-H "Content-Type: application/json" \\'}</div>
                <div className="text-green-400 mb-2 pl-8">{'-d \'{"sku": "PROD-001", "qty": 10}\' \\'}</div>
                <div className="text-green-400 mb-2 pl-8">"https://api.chesiwms.com/v1/inventory"</div>
                <div className="text-gray-400 mb-4 mt-6">{'{'}</div>
                <div className="text-gray-400 mb-1 pl-4">"status": "success",</div>
                <div className="text-gray-400 mb-1 pl-4">"message": "Inventory updated",</div>
                <div className="text-gray-400 mb-1 pl-4">"data": {'{'}</div>
                <div className="text-gray-400 mb-1 pl-8">"sku": "PROD-001",</div>
                <div className="text-gray-400 mb-1 pl-8">"location": "A-12-4",</div>
                <div className="text-gray-400 mb-1 pl-8">"qty": 10</div>
                <div className="text-gray-400 mb-1 pl-4">{'}'}</div>
                <div className="text-gray-400">{'} '}<span className="terminal-cursor">|</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black to-gray-900"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="holographic rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              Your Digital Warehouse Starts Now.
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-300 mx-auto">
              Join hundreds of businesses across Africa that have transformed their operations with ChesiWMS.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <a href="#pricing" className="btn-gold px-8 py-4 rounded-md text-lg font-medium">
                Start Free Trial
              </a>
              <a href="#" className="btn-ghost px-8 py-4 rounded-md text-lg font-medium">
                Talk to Sales
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
            <div className="col-span-2">
              <img src={chesiLogo} alt="ChesiWMS Logo" className="h-10" />
              <p className="mt-4 text-gray-400">
                Next-generation warehouse management for Africa's growing businesses.
              </p>
              <div className="mt-4">
                <a href="mailto:info@chesimobile.com" className="text-gray-400 hover:text-gold flex items-center">
                  <i className="fas fa-envelope mr-2"></i>
                  <span>info@chesimobile.com</span>
                </a>
              </div>
              <div className="mt-6 flex space-x-6">
                <a href="#" className="text-gray-400 hover:text-gold">
                  <i className="fab fa-twitter text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-gold">
                  <i className="fab fa-linkedin text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-gold">
                  <i className="fab fa-youtube text-xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gold tracking-wider uppercase">Product</h3>
              <ul className="mt-4 space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white">Features</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Pricing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Integrations</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Roadmap</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gold tracking-wider uppercase">Resources</h3>
              <ul className="mt-4 space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white">Documentation</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">API Reference</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Support</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gold tracking-wider uppercase">Company</h3>
              <ul className="mt-4 space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white">About</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Careers</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Privacy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white">Terms</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gold tracking-wider uppercase">Download the App</h3>
              <div className="mt-4">
                <a 
                  href="/chesiwms-app.apk" 
                  download="chesiwms-app.apk"
                  className="inline-flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  aria-label="Download ChesiWMS Android App"
                >
                  <svg 
                    className="w-6 h-6" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5506 0 .9993.4486.9993.9993.0001.5511-.4486.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5506 0 .9993.4486.9993.9993 0 .5511-.4486.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1349 1.2297L4.8429 5.5775a.4161.4161 0 00-.5676-.1521.416.416 0 00-.1521.5676l1.9973 3.4592C2.6889 11.186.8535 12.3064.8535 13.8c0 2.02 3.6405 3.6 11.1465 3.6 7.5059 0 11.1465-1.58 11.1465-3.6 0-1.4936-1.8354-2.6136-4.2285-3.4786"/>
                  </svg>
                  <span className="text-sm">Android</span>
                </a>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between">
            <p className="text-gray-400 text-sm">
              &copy; 2025 Chesi Mobile Solutions Ltd. All rights reserved.
            </p>
            <div className="mt-4 md:mt-0">
              <p className="text-gray-400 text-sm">
                Made with <i className="fas fa-heart text-red-500"></i> in Nairobi
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default AppContent;
